import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditorRef } from 'platejs/react';
import { FindReplacePlugin } from '@platejs/find-replace';
import styles from './FindReplaceDialog.module.scss';

interface FindReplaceDialogProps {
    open: boolean;
    onClose: () => void;
    rootRef?: React.RefObject<HTMLElement>;
}

// Walks the editor value and collects every match of `search` (case-insensitive)
// as a list of leaf-relative ranges. Mirrors the way decorateFindReplace iterates
// so the order matches what the user sees highlighted.
function findAllMatches(editor: any, search: string): Array<{ path: number[]; offset: number; length: number }> {
    if (!editor || !search) return [];
    const lowerSearch = search.toLowerCase();
    const matches: Array<{ path: number[]; offset: number; length: number }> = [];
    const walk = (nodes: any[], parentPath: number[]) => {
        if (!Array.isArray(nodes)) return;
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            const path = [...parentPath, i];
            if (node?.children && Array.isArray(node.children) && node.children.every((c: any) => typeof c?.text === 'string')) {
                const texts: string[] = node.children.map((c: any) => c.text);
                const joined = texts.join('');
                const joinedLower = joined.toLowerCase();
                let start = 0;
                while (true) {
                    const idx = joinedLower.indexOf(lowerSearch, start);
                    if (idx === -1) break;
                    let cumulative = 0;
                    for (let ti = 0; ti < texts.length; ti++) {
                        const t = texts[ti];
                        const textStart = cumulative;
                        const textEnd = cumulative + t.length;
                        if (idx >= textStart && idx < textEnd) {
                            const offset = idx - textStart;
                            const lengthInThisText = Math.min(t.length - offset, lowerSearch.length);
                            matches.push({ path: [...path, ti], offset, length: lengthInThisText });
                            break;
                        }
                        cumulative = textEnd;
                    }
                    start = idx + lowerSearch.length;
                }
            } else if (Array.isArray(node?.children)) {
                walk(node.children, path);
            }
        }
    };
    walk(editor.children ?? [], []);
    return matches;
}

// Replaces every occurrence of `search` (case-insensitive) with `replace`
// across the entire editor value. Walks the same way as findAllMatches so
// counts agree. Uses `editor.tf.delete` + `editor.tf.insertText` keyed by
// path to keep operations on the real Slate tree (history-friendly).
function replaceAllInEditor(editor: any, search: string, replace: string): number {
    if (!editor || !search) return 0;
    let count = 0;
    // Collect matches first, but apply right-to-left within each path so earlier
    // offsets stay stable as we mutate.
    const allMatches = findAllMatches(editor, search);
    if (allMatches.length === 0) return 0;
    const byPathKey = new Map<string, Array<{ offset: number; length: number }>>();
    for (const m of allMatches) {
        const key = m.path.join('.');
        if (!byPathKey.has(key)) byPathKey.set(key, []);
        byPathKey.get(key)!.push({ offset: m.offset, length: m.length });
    }
    for (const [pathKey, list] of byPathKey.entries()) {
        const path = pathKey.split('.').map(Number);
        list.sort((a, b) => b.offset - a.offset);
        for (const m of list) {
            try {
                const range = {
                    anchor: { path, offset: m.offset },
                    focus: { path, offset: m.offset + m.length },
                };
                editor.tf.delete?.({ at: range });
                editor.tf.insertText?.(replace, { at: { path, offset: m.offset } });
                count += 1;
            } catch {
                /* defensive: range may have shifted due to a previous mutation; skip */
            }
        }
    }
    return count;
}

export function FindReplaceDialog({ open, onClose, rootRef }: FindReplaceDialogProps) {
    const editor = useEditorRef();
    const [findText, setFindText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const findInputRef = useRef<HTMLInputElement>(null);

    // Keep the plugin's search option in sync. Empty string disables highlights.
    useEffect(() => {
        if (!editor) return;
        try {
            (editor as any).setOption?.(FindReplacePlugin, 'search', open ? findText : '');
        } catch {
            /* defensive: plugin may not be registered in some test harnesses */
        }
    }, [editor, open, findText]);

    const matches = useMemo(() => {
        if (!open || !findText) return [];
        return findAllMatches(editor, findText);
    }, [editor, open, findText]);

    useEffect(() => {
        if (matches.length === 0) {
            setActiveIndex(0);
            return;
        }
        // Clamp the active index when matches change so the counter doesn't
        // outrun the list (e.g. after a Replace removes the current match).
        setActiveIndex((idx) => Math.min(idx, matches.length - 1));
    }, [matches.length]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => findInputRef.current?.focus(), 0);
        return () => clearTimeout(t);
    }, [open]);

    // Mark the currently-active match in the rendered DOM via a data attribute.
    // Decorations land as `<span data-slate-search-highlight="true">` — we
    // additionally tag the nth one with `data-current-match="true"` for the
    // outlined orange highlight (see SCSS).
    useEffect(() => {
        if (!open) return;
        const root = rootRef?.current ?? document;
        const nodes = (root as any).querySelectorAll('[data-slate-search-highlight="true"]');
        nodes.forEach((el: HTMLElement, i: number) => {
            if (i === activeIndex) el.setAttribute('data-current-match', 'true');
            else el.removeAttribute('data-current-match');
        });
        return () => {
            nodes.forEach((el: HTMLElement) => el.removeAttribute('data-current-match'));
        };
    });

    const focusMatch = useCallback(
        (nextIndex: number) => {
            const ed: any = editor;
            const m = matches[nextIndex];
            if (!m || !ed) return;
            try {
                ed.tf.select?.({
                    anchor: { path: m.path, offset: m.offset },
                    focus: { path: m.path, offset: m.offset + m.length },
                });
            } catch {
                /* defensive: path may have shifted */
            }
        },
        [editor, matches],
    );

    const handleFindNext = useCallback(() => {
        if (matches.length === 0) return;
        const next = (activeIndex + 1) % matches.length;
        setActiveIndex(next);
        focusMatch(next);
    }, [activeIndex, matches.length, focusMatch]);

    const handleFindPrev = useCallback(() => {
        if (matches.length === 0) return;
        const prev = (activeIndex - 1 + matches.length) % matches.length;
        setActiveIndex(prev);
        focusMatch(prev);
    }, [activeIndex, matches.length, focusMatch]);

    const handleReplace = useCallback(() => {
        if (matches.length === 0) return;
        const m = matches[activeIndex];
        if (!m) return;
        const ed: any = editor;
        try {
            const range = {
                anchor: { path: m.path, offset: m.offset },
                focus: { path: m.path, offset: m.offset + m.length },
            };
            ed?.tf?.delete?.({ at: range });
            ed?.tf?.insertText?.(replaceText, { at: { path: m.path, offset: m.offset } });
        } catch {
            /* defensive: range may have shifted between the find and the replace */
        }
    }, [editor, matches, activeIndex, replaceText]);

    const handleReplaceAll = useCallback(() => {
        if (!findText) return;
        replaceAllInEditor(editor, findText, replaceText);
    }, [editor, findText, replaceText]);

    if (!open) return null;

    return (
        <div
            className={styles.dialog}
            role="dialog"
            aria-label="Find and replace"
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                }
                if (e.key === 'Enter') {
                    e.preventDefault();
                    if (e.shiftKey) handleFindPrev();
                    else handleFindNext();
                }
            }}
        >
            <div className={styles.row}>
                <input
                    ref={findInputRef}
                    className={styles.input}
                    placeholder="Find"
                    value={findText}
                    onChange={(e) => setFindText(e.target.value)}
                    aria-label="Find text"
                />
                <span className={styles.counter} aria-live="polite">
                    {matches.length === 0 ? '0 of 0' : `${activeIndex + 1} of ${matches.length}`}
                </span>
            </div>
            <div className={styles.row}>
                <input
                    className={styles.input}
                    placeholder="Replace"
                    value={replaceText}
                    onChange={(e) => setReplaceText(e.target.value)}
                    aria-label="Replace with"
                />
            </div>
            <div className={styles.actions}>
                <div className={styles.left}>
                    <button
                        type="button"
                        className={styles.btn}
                        onClick={handleFindPrev}
                        disabled={matches.length === 0}
                        aria-label="Find previous"
                    >
                        Prev
                    </button>
                    <button
                        type="button"
                        className={styles.btn}
                        onClick={handleFindNext}
                        disabled={matches.length === 0}
                        aria-label="Find next"
                    >
                        Next
                    </button>
                </div>
                <div className={styles.right}>
                    <button
                        type="button"
                        className={styles.btn}
                        onClick={handleReplace}
                        disabled={matches.length === 0}
                        aria-label="Replace current"
                    >
                        Replace
                    </button>
                    <button
                        type="button"
                        className={styles.btn}
                        onClick={handleReplaceAll}
                        disabled={matches.length === 0}
                        aria-label="Replace all"
                    >
                        Replace all
                    </button>
                    <button type="button" className={styles.btn} onClick={onClose} aria-label="Close find and replace">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
